// 📜 SettingsModal.js
import React, { useState, useEffect } from 'react';
import { db, appId } from './firebase-config';
import { collection, getDocs, writeBatch, doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

// Componente de Icono (asumiendo que está definido en App.js o un archivo compartido)
const Icon = ({ path, className = "w-6 h-6", ...props }) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className} {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d={path} />
    </svg>
);

// --- Componente para configurar la contraseña del chofer ---
const LogisticsSettingsPanel = ({ showMessage }) => {
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchPassword = async () => {
            if (!db || !appId) return;
            const settingsDocRef = doc(db, `artifacts/${appId}/logisticsSettings`, 'main');
            try {
                const settingsDoc = await getDoc(settingsDocRef);
                if(settingsDoc.exists()) {
                    setPassword(settingsDoc.data().transporterPassword || '');
                }
            } catch (error) {
                console.error("Error fetching password:", error);
            }
        };
        fetchPassword();
    }, []);
    
    const handleSave = async () => {
        setLoading(true);
        try {
            const settingsDocRef = doc(db, `artifacts/${appId}/logisticsSettings`, 'main');
            await setDoc(settingsDocRef, { transporterPassword: password }, { merge: true });
            showMessage('Contraseña de chofer guardada con éxito.', 'success');
        } catch (error) {
            showMessage('Error al guardar la contraseña.', 'error');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-4 p-4 border rounded-lg bg-slate-50 mt-4">
            <h4 className="font-semibold text-lg text-slate-800">Acceso de Choferes</h4>
            <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Contraseña de Logística:</label>
                <input
                    type="text"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Establecer contraseña"
                    className="w-full p-2 border rounded-md"
                />
            </div>
            <button onClick={handleSave} disabled={loading} className="w-full bg-blue-600 text-white py-2 px-4 rounded-md disabled:bg-blue-300">
                {loading ? 'Guardando...' : 'Guardar Contraseña'}
            </button>
        </div>
    );
};


// --- Nuevo Componente para configurar contraseñas individuales (dentro del modal principal) ---
const UserPasswordManagement = ({ allUsers, showMessage }) => {
    const [userPasswords, setUserPasswords] = useState({});
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState({});
    const [showUserPassword, setShowUserPassword] = useState({});
    const [error, setError] = useState({});

    useEffect(() => {
        const fetchUserPasswords = async () => {
            if (!db || !appId) return;
            const passwords = {};
            for (const user of allUsers) {
                const userDocRef = doc(db, 'users', user.uid);
                const userDoc = await getDoc(userDocRef);
                if (userDoc.exists()) {
                    passwords[user.uid] = userDoc.data().adminPassword || '';
                }
            }
            setUserPasswords(passwords);
        };
        fetchUserPasswords();
    }, [allUsers]);

    const handlePasswordChange = (uid, newPassword) => {
        setUserPasswords(prev => ({ ...prev, [uid]: newPassword }));
        setError(prev => ({...prev, [uid]: ''}));
    };

    const handleSavePassword = async (uid) => {
        setLoading(true);
        setStatus(prev => ({ ...prev, [uid]: 'loading' }));
        setError(prev => ({...prev, [uid]: ''}));

        if (!userPasswords[uid] || userPasswords[uid].length < 4) {
             showMessage("La contraseña debe tener al menos 4 caracteres.", 'error');
             setError(prev => ({ ...prev, [uid]: "La contraseña debe tener al menos 4 caracteres." }));
             setStatus(prev => ({ ...prev, [uid]: 'error' }));
             setLoading(false);
             setTimeout(() => setStatus(prev => ({ ...prev, [uid]: '' })), 3000);
             return;
        }

        try {
            const userDocRef = doc(db, 'users', uid);
            await updateDoc(userDocRef, { adminPassword: userPasswords[uid] });
            showMessage(`Contraseña para ${allUsers.find(u => u.uid === uid)?.email} guardada.`, 'success');
            setStatus(prev => ({ ...prev, [uid]: 'success' }));
        } catch (error) {
            console.error("Error al guardar contraseña:", error);
            showMessage(`Error al guardar la contraseña para ${allUsers.find(u => u.uid === uid)?.email}.`, 'error');
            setStatus(prev => ({ ...prev, [uid]: 'error' }));
        } finally {
            setLoading(false);
            setTimeout(() => setStatus(prev => ({ ...prev, [uid]: '' })), 3000);
        }
    };

    return (
        <div className="p-4 border rounded-lg">
            <h4 className="font-bold text-lg mb-2">Gestión de Contraseñas de Usuarios</h4>
            <div className="space-y-4">
                {allUsers.map(user => (
                    <div key={user.uid} className="flex flex-col sm:flex-row items-center justify-between p-3 bg-slate-100 rounded-lg shadow-sm">
                        <div className="text-sm font-medium text-slate-800 mb-2 sm:mb-0 w-full sm:w-1/3 truncate">
                            {user.email}
                            <span className="font-semibold capitalize ml-2 p-1 px-2 rounded-full text-xs text-white bg-slate-700">{user.role}</span>
                        </div>
                        <div className="w-full sm:w-2/3 flex flex-col sm:flex-row items-center gap-2">
                            <div className="relative w-full">
                                <input
                                    type={showUserPassword[user.uid] ? "text" : "password"}
                                    value={userPasswords[user.uid] || ''}
                                    onChange={(e) => handlePasswordChange(user.uid, e.target.value)}
                                    placeholder="Establecer contraseña individual"
                                    className="w-full p-2 pr-10 border rounded-md"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowUserPassword(prev => ({ ...prev, [user.uid]: !prev[user.uid] }))}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
                                    title={showUserPassword[user.uid] ? "Ocultar contraseña" : "Mostrar contraseña"}
                                >
                                    <Icon 
                                        path={showUserPassword[user.uid] ? "M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0112 4.5c4.756 0 8.774 3.162 10.066 7.476-1.092 3.821-4.104 6.49-7.533 8.22M15 12a3 3 0 11-6 0 3 3 0 016 0zm-6 0a3 3 0 006 0" : "M2.036 12.328a.9.9 0 000 1.344S4.325 17.5 12 17.5c7.675 0 9.964-3.828 9.964-3.828a.9.9 0 000-1.344S19.675 8.5 12 8.5c-7.675 0-9.964 3.828-9.964 3.828zM12 12a3 3 0 100-6 3 3 0 000 6z"}
                                        className="w-5 h-5 text-slate-400 hover:text-slate-600 transition-colors"
                                    />
                                </button>
                            </div>
                            <button
                                onClick={() => handleSavePassword(user.uid)}
                                disabled={loading || !userPasswords[user.uid]}
                                className={`shrink-0 p-2 rounded-full transition-colors ${status[user.uid] === 'loading' ? 'bg-blue-300' : 'bg-blue-500 hover:bg-blue-600'}`}
                            >
                                <Icon path={status[user.uid] === 'loading' ? "M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0011.667 0l3.181-3.183m-4.991-2.691v4.992h4.992" : "M4.5 12.75l6 6 9-13.5"} className={`w-5 h-5 text-white ${status[user.uid] === 'loading' ? 'animate-spin' : ''}`}/>
                            </button>
                        </div>
                         {error[user.uid] && <p className="text-sm text-red-500 mt-2">{error[user.uid]}</p>}
                    </div>
                ))}
            </div>
        </div>
    );
};


const SettingsModal = ({ show, onClose, soundEnabled, onToggleSound, showMessage, allUsers, currentUserData }) => {
    const [unlockLoading, setUnlockLoading] = useState(false);

    const handleUnlockControls = async () => {
        setUnlockLoading(true);
        showMessage("Desbloqueando controles trabados...", "info");
        try {
            const productsRef = collection(db, `artifacts/${appId}/products`);
            const productsSnapshot = await getDocs(productsRef);
            const batch = writeBatch(db);
            let unlockedCount = 0;

            productsSnapshot.forEach(productDoc => {
                const productData = productDoc.data();
                if (productData.controlStatus?.status === 'in_control') {
                    const productRef = doc(db, `artifacts/${appId}/products`, productDoc.id);
                    batch.update(productRef, { "controlStatus.status": "available" });
                    unlockedCount++;
                }
            });

            if (unlockedCount > 0) {
                await batch.commit();
                showMessage(`${unlockedCount} control(es) desbloqueado(s) exitosamente.`, "success");
            } else {
                showMessage("No se encontraron controles para desbloquear.", "info");
            }
        } catch (error) {
            console.error("Error desbloqueando controles:", error);
            showMessage("Ocurrió un error al intentar desbloquear los controles.", "error");
        } finally {
            setUnlockLoading(false);
        }
    };

    if (!show) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="p-4 border-b flex justify-between items-center">
                    <h3 className="text-2xl font-bold text-slate-900">Configuración</h3>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-200"><Icon path="M6 18L18 6M6 6l12 12" className="w-6 h-6"/></button>
                </div>
                
                <div className="overflow-y-auto p-6 space-y-8">
                    {/* Sección de Sonido */}
                    <div className="p-4 border rounded-lg">
                        <h4 className="font-bold text-lg mb-2">Sonido</h4>
                        <div className="flex items-center justify-between">
                            <span>Habilitar sonidos de la aplicación</span>
                            <button onClick={onToggleSound} className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors ${soundEnabled ? 'bg-blue-600' : 'bg-gray-200'}`}>
                                <span className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${soundEnabled ? 'translate-x-6' : 'translate-x-1'}`}/>
                            </button>
                        </div>
                    </div>

                    {/* Sección de Controles (Solo para Admin) */}
                    {currentUserData?.role === 'admin' && (
                        <>
                            <div className="p-4 border rounded-lg">
                                <h4 className="font-bold text-lg mb-2">Administración</h4>
                                <div className="flex items-center justify-between">
                                    <p className="text-sm text-gray-600">
                                        Si un control de stock quedó "En Progreso" por error,<br/>
                                        puedes forzar su desbloqueo para todos los usuarios.
                                    </p>
                                    <button onClick={handleUnlockControls} disabled={unlockLoading} className="bg-red-500 text-white font-semibold py-2 px-4 rounded-md hover:bg-red-600 disabled:bg-red-300">
                                        {unlockLoading ? 'Desbloqueando...' : 'Desbloquear Controles'}
                                    </button>
                                </div>
                            </div>

                            {/* Sección de Usuarios (Solo para Admin) */}
                            <UserPasswordManagement allUsers={allUsers} showMessage={showMessage} />
                            
                            {/* Panel de configuración de contraseña para choferes */}
                            <LogisticsSettingsPanel showMessage={showMessage} />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SettingsModal;